import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Plane } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        await axios.post(`${API}/auth/register`, formData);
        toast.success("Registro exitoso. Ahora inicia sesión.");
        setIsRegister(false);
        setFormData({ username: formData.username, email: "", password: "" });
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          username: formData.username,
          password: formData.password
        });
        onLogin(response.data.access_token);
        toast.success("Inicio de sesión exitoso");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la operación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #0c4a6e 0%, #0369a1 40%, #0284c7 70%, #38bdf8 100%)"
      }}
    >
      {/* Decorative floating circles */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-sky-300/20 rounded-full blur-3xl animate-float" />
      <div
        className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl animate-float"
        style={{ animationDelay: "2s" }}
      />

      <Card
        className="w-full max-w-md shadow-2xl border-0 glass-card relative z-10"
        data-testid="admin-login-card"
      >
        <CardHeader className="space-y-2 text-center pt-10">
          <div className="flex justify-center mb-3">
            <div className="btn-sky p-4 rounded-2xl shadow-lg">
              <Plane className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle
            className="text-3xl font-bold font-heading text-sky-900"
            data-testid="login-title"
          >
            {isRegister ? "Crear Cuenta" : "Panel Administrativo"}
          </CardTitle>
          <CardDescription className="text-sky-700/70 text-base">
            {isRegister ? "Registra tu agencia de viajes" : "Accede al backoffice"}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-10 px-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sky-900 font-medium">
                {isRegister ? "Usuario" : "Usuario o email"}
              </Label>
              <Input
                id="username"
                data-testid="username-input"
                type="text"
                placeholder={isRegister ? "tu_usuario" : "usuario o tu@email.com"}
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                className="border-sky-200 focus-visible:ring-sky-500 h-11"
              />
            </div>

            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sky-900 font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="border-sky-200 focus-visible:ring-sky-500 h-11"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sky-900 font-medium">
                Contraseña
              </Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="border-sky-200 focus-visible:ring-sky-500 h-11"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-sky text-white h-11 text-base font-semibold"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? "Procesando..." : isRegister ? "Registrarse" : "Iniciar Sesión"}
            </Button>
          </form>

          <div className="mt-5 text-center text-sm">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-sky-700 hover:text-sky-900 hover:underline font-medium transition-colors"
              data-testid="toggle-mode-button"
            >
              {isRegister ? "¿Ya tienes cuenta? Inicia sesión" : "¿Primera vez? Regístrate"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
